import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Zap, ArrowRight, Shield, Clock, RefreshCw, Bot, Brain, Sparkles } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { apiClient } from '../apiClient';
import AuthContext from '../context/AuthContext';
import { SKOOL_SIGNUP_URL } from '../config/externalLinks';

function PricingPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [stats, setStats] = useState({
    founder_spots_remaining: null,
    founder_pricing_active: true
  });

  useEffect(() => {
    apiClient.get('/api/public/stats')
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  const handleCheckout = async (plan) => {
    if (!user) {
      window.open(SKOOL_SIGNUP_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    setLoading(plan);
    try {
      const { checkout_url } = await apiClient.post('/api/billing/checkout', { plan });
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        alert('Unable to start checkout. Please try again.');
      }
    } catch (err) {
      if (err?.status === 401) {
        navigate(`/login?redirect=${encodeURIComponent('/pricing')}`);
        return;
      }
      console.error('Checkout error:', err);
      alert('Unable to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="pricing-nav border-b border-white/5">
        <nav className="pricing-nav-inner max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark size={40} />
            <span className="marketing-brand-title font-bold text-xl tracking-tight">REBEL PROMPTS</span>
          </Link>
          <div className="marketing-nav-actions flex items-center gap-4">
            {user ? (
              <Link to="/profile" className="marketing-nav-link text-gray-400 hover:text-white transition-colors">Open App</Link>
            ) : (
              <Link to="/login" className="marketing-nav-link text-gray-400 hover:text-white transition-colors">Sign In</Link>
            )}
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {stats.founder_pricing_active && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rebel-cyan/10 border border-rebel-cyan/20 mb-6">
              <Clock className="w-4 h-4 text-rebel-cyan" />
              <span className="text-sm text-rebel-cyan">
                Founder Pricing - {stats.founder_spots_remaining !== null ? `${stats.founder_spots_remaining.toLocaleString()} spots left` : 'Limited time'}
              </span>
            </div>
          )}
          <h1 className="text-3xl md:text-5xl font-black mb-4">
            How much is your time worth?
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-3">
            Every hour you spend hunting for lost prompts or rewriting what you've already built is an hour you'll never get back.
          </p>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Rebel Prompts pays for itself the first time it saves you from starting over.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Starter Plan */}
            <div className="card-glass p-8 relative">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-rebel-red/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-rebel-red" />
                  </div>
                  <h3 className="text-2xl font-bold">Starter</h3>
                </div>
                <p className="text-gray-400">Your prompt library, organized and version-controlled. Never lose your work again.</p>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-gray-500 line-through text-xl">$27</span>
                  <span className="text-5xl font-bold">$17</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-rebel-cyan mt-1">Lock in $120/year savings forever</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Unlimited prompts',
                  'Projects & folders',
                  'Variable templates ({{topic}}, {{style}}, etc.)',
                  'Full version history',
                  'Fork any public prompt',
                  'Community library access',
                  '20 AI credits/month',
                  'Email support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-rebel-cyan flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCheckout('starter')}
                disabled={loading === 'starter'}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                {loading === 'starter' ? 'Loading...' : 'Start Organizing'}
                <ArrowRight className="w-4 h-4" />
              </button>
              
              <p className="text-center text-xs text-gray-600 mt-4">
                Perfect for solopreneurs and creators
              </p>
            </div>

            {/* Pro Plan */}
            <div className="card-glass p-8 relative border-rebel-cyan/30 bg-gradient-to-b from-rebel-cyan/5 to-transparent">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 rounded-full bg-rebel-cyan text-black text-sm font-semibold">
                  Most Popular
                </span>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-rebel-blue/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-rebel-blue" />
                  </div>
                  <h3 className="text-2xl font-bold">Pro</h3>
                </div>
                <p className="text-gray-400">Everything in Starter, plus Agent Instructions API. Update AI behavior without deploying.</p>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-gray-500 line-through text-xl">$47</span>
                  <span className="text-5xl font-bold">$27</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-rebel-cyan mt-1">Lock in $240/year savings forever</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'Everything in Starter',
                  'Agent Instructions API',
                  'Dev & Live environments',
                  '55 AI credits/month',
                  'AI-powered prompt generation',
                  'Team collaboration',
                  'Full API access',
                  'Priority support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-rebel-cyan flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCheckout('pro')}
                disabled={loading === 'pro'}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading === 'pro' ? 'Loading...' : 'Go Pro'}
                <Zap className="w-4 h-4" />
              </button>
              
              <p className="text-center text-xs text-gray-600 mt-4">
                For developers, teams, and power users
              </p>
            </div>
          </div>

          {/* Trust Signals */}
          <div className="mt-12 text-center">
            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-500 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Secure via Stripe</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>Instant access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Reinforcement */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-6">Think about it...</h2>
          <div className="grid sm:grid-cols-3 gap-6 text-left">
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-3xl font-bold text-rebel-cyan mb-2">30 min</p>
              <p className="text-gray-400 text-sm">Average time spent searching for a lost prompt</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-3xl font-bold text-rebel-cyan mb-2">2+ hours</p>
              <p className="text-gray-400 text-sm">Time to recreate a complex prompt from scratch</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <p className="text-3xl font-bold text-rebel-cyan mb-2">$17/mo</p>
              <p className="text-gray-400 text-sm">Cost of never losing a prompt again</p>
            </div>
          </div>
          <p className="text-gray-500 mt-8">
            One saved prompt pays for a month. Everything after that is pure ROI.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10">Quick answers</h2>
          
          <div className="space-y-6">
            {[
              {
                q: "What happens after I sign up?",
                a: "For now, new members join through Skool first. After joining there, you can access Rebel Prompts through the member flow."
              },
              {
                q: "Can I switch plans later?",
                a: "Yes. Upgrade or downgrade anytime. Your founder pricing stays locked in."
              },
              {
                q: "What are AI credits?",
                a: "Credits for AI-powered features like prompt generation and improvement suggestions. Starter gets 20/month, Pro gets 55/month."
              },
              {
                q: "What if I need to cancel?",
                a: "Cancel anytime from your settings. No questions, no hoops. Your data stays exportable."
              },
              {
                q: "What's the Agent Instructions API?",
                a: "Pro feature. Your AI agents can pull their system instructions via API, so you can update their behavior without redeploying code."
              }
            ].map((faq, i) => (
              <div key={i} className="pb-6 border-b border-white/5 last:border-0">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm">
              More questions? <Link to="/support" className="text-rebel-cyan hover:underline">Contact us</Link> or check our <Link to="/docs" className="text-rebel-cyan hover:underline">documentation</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to stop losing your best work?
          </h2>
          <p className="text-gray-400 mb-8">
            Join the AI builders who've decided their prompts are worth keeping.
          </p>
          <button
            onClick={() => handleCheckout('starter')}
            className="btn-primary text-lg px-8 py-4 flex items-center gap-2 mx-auto"
          >
            Get Started Now <ArrowRight className="w-5 h-5" />
          </button>
          {stats.founder_pricing_active && stats.founder_spots_remaining !== null && (
            <p className="text-sm text-gray-600 mt-4">
              {stats.founder_spots_remaining.toLocaleString()} founder spots remaining
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-gray-600 text-sm">(c) 2026 Rebel Prompts. All rights reserved.</span>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-gray-500 hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="text-gray-500 hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PricingPage;
